/**
 * The account, and the door into the world.
 *
 * Two steps that are deliberately separate. Logging in says who you are and
 * hands back a token; joining says where you stand, and needs a world to be
 * live. Keeping them apart means the client can tell "wrong password" from
 * "the server has no world yet", which are two very different things to be
 * told at a login screen.
 *
 * The token lives in `sessionStorage`: it survives a reload — which happens
 * constantly while the client is being worked on — and dies with the tab. Not
 * `localStorage`, because a token that outlives the browser being closed is a
 * token sitting on the disk of whatever machine the game was opened on.
 */

/**
 * Where the server is.
 *
 * **Relative by default**, because production serves the app and proxies the
 * API from the same origin: a same-origin request has no preflight and no CORS
 * to get wrong, which is a class of failure this project has already paid for
 * once. `VITE_API_URL` stays as an override for pointing a dev build at a
 * server somewhere else.
 */
const API = import.meta.env.VITE_API_URL ?? '';

const STORAGE_KEY = 'winged.session';

export interface Session {
  token: string;
  playerId: string;
  /** The name in the world; empty until it has been chosen. */
  username: string;
  email: string;
  /** Whether the next thing to ask for is a nickname. */
  needsNickname: boolean;
  expiresAt: string;
}

export interface Joined {
  playerId: string;
  username: string;
  world: {
    id: string;
    chunkRadius: number;
    latticeRadius: number;
    maxElevation: number;
    maxDepth: number;
    chunkCount: number;
    cellsPerChunk: number;
  };
  position: { q: number; r: number };
  /**
   * Where the camera was left last time, or null on a first visit.
   *
   * Kept by the server beside the position, because it is the same kind of
   * fact: this player, in this world. Coming back to the view you left is the
   * difference between returning and starting again.
   */
  view: { yaw: number; zoom: number } | null;
  /** Whether the server had to invent a starting cell — a first visit. */
  fresh: boolean;
}

export function storedSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const session = JSON.parse(raw) as Session;
    // An expired token would be refused on the first request anyway; checking
    // here means the login screen appears instead of an error behind it.
    return Date.parse(session.expiresAt) > Date.now() ? session : null;
  } catch {
    return null;
  }
}

function remember(session: Session): Session {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Private browsing refuses storage. Losing the session on reload is a
    // smaller problem than refusing to start the game.
  }
  return session;
}

export function forget(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // As above.
  }
}

async function post(path: string, body: unknown, token?: string): Promise<Response> {
  const response = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response;
}

/**
 * A new account: address, password and an **invitation code**.
 *
 * The code is what makes registration a door rather than a hole — one code, one
 * account, and the panel is where they come from. The account comes back
 * without a nickname; choosing one is the next step and the world will not let
 * anybody in until it is done.
 */
export const register = async (email: string, password: string, code: string) =>
  remember((await (await post('/api/auth/register', { email, password, code })).json()) as Session);

export const logIn = async (email: string, password: string) =>
  remember((await (await post('/api/auth/login', { email, password })).json()) as Session);

/** Choose the name everybody else sees. Once — after that it takes an admin. */
export async function chooseNickname(session: Session, nickname: string): Promise<Session> {
  await post('/api/auth/nickname', { nickname }, session.token);
  return remember({ ...session, username: nickname, needsNickname: false });
}

/**
 * Ask for a password-reset link.
 *
 * Answers the same way whatever the address is — there is no version of this
 * that tells the caller whether an account exists, on purpose.
 */
export async function requestReset(email: string): Promise<void> {
  await post('/api/auth/password/reset', { email });
}

/** Spend a reset link. Every session of that account ends with it. */
export async function setPassword(token: string, password: string): Promise<void> {
  await post('/api/auth/password/set', { token, password });
}

/**
 * The reset token out of the address bar, if the page was opened from a link.
 *
 * Removed from the URL as soon as it is read: a token sitting in the address
 * bar ends up in the history of the browser and in every screenshot taken
 * afterwards.
 */
export function resetTokenFromUrl(): string | null {
  const found = new URLSearchParams(window.location.search).get('reset');
  if (!found) return null;

  const url = new URL(window.location.href);
  url.searchParams.delete('reset');
  window.history.replaceState({}, '', url.toString());
  return found;
}

/** Where this player stands in the live world, spawning them on a first visit. */
export async function join(session: Session): Promise<Joined> {
  const response = await fetch(`${API}/api/world/join`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.token}` },
  });

  if (!response.ok) {
    throw new Error(`${response.status} ${await response.text()}`);
  }

  return (await response.json()) as Joined;
}

export async function logOut(session: Session): Promise<void> {
  forget();
  try {
    await fetch(`${API}/api/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.token}` },
    });
  } catch {
    // The token is gone from this browser either way; the server dropping it is
    // tidiness, not correctness.
  }
}
